import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Generation {
  id: string;
  tool_id: string;
  user_id?: string;
  email?: string; // for anonymous generations
  title?: string;
  public_url_slug?: string;
  generated_content?: any;
  configuration?: any;
  generation_type: 'survey' | 'conversation';
  version: number;
  view_count: number;
  share_count: number;
  last_viewed_at?: string;
  estimated_duration_minutes?: number;
  status: 'draft' | 'active' | 'archived';
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface GenerationAnalytics {
  id: string;
  generation_id: string;
  event_type: string;
  event_data?: any;
  user_id?: string;
  created_at: string;
}

export interface GenerationVersion {
  id: string;
  generation_id: string;
  version_number: number;
  generated_content?: any;
  configuration?: any;
  change_description?: string;
  created_at: string;
  created_by?: string;
}

// Hook to get user's generations
export const useUserGenerations = (userId?: string, type?: 'survey' | 'conversation') => {
  return useQuery({
    queryKey: ['user-generations', userId, type],
    queryFn: async () => {
      let query = supabase
        .from('generations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }

      if (type) {
        query = query.eq('generation_type', type);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Generation[];
    },
    enabled: !!userId
  });
};

// Hook to get a single generation by ID
export const useGeneration = (generationId: string) => {
  return useQuery({
    queryKey: ['generation', generationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generations')
        .select('*')
        .eq('id', generationId)
        .single();

      if (error) throw error;
      return data as Generation;
    },
    enabled: !!generationId
  });
};

// Hook to get generation by public slug
export const usePublicGeneration = (slug: string) => {
  return useQuery({
    queryKey: ['public-generation', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generations')
        .select('*')
        .eq('public_url_slug', slug)
        .eq('is_public', true)
        .single();

      if (error) throw error;

      // Track view event
      if (data) {
        await supabase.rpc('track_generation_event', {
          generation_id: data.id,
          event_type: 'view'
        });
      }

      return data as Generation;
    },
    enabled: !!slug
  });
};

// Hook to get generation versions
export const useGenerationVersions = (generationId: string) => {
  return useQuery({
    queryKey: ['generation-versions', generationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generation_versions')
        .select('*')
        .eq('generation_id', generationId)
        .order('version_number', { ascending: false });

      if (error) throw error;
      return data as GenerationVersion[];
    },
    enabled: !!generationId
  });
};

// Hook to get generation analytics
export const useGenerationAnalytics = (generationId: string) => {
  return useQuery({
    queryKey: ['generation-analytics', generationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generation_analytics')
        .select('*')
        .eq('generation_id', generationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as GenerationAnalytics[];
    },
    enabled: !!generationId
  });
};

// Hook to create a generation
export const useCreateGeneration = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (generation: Partial<Generation> & { tool_id: string }) => {
      const { data, error } = await supabase
        .from('generations')
        .insert([generation])
        .select()
        .single();

      if (error) throw error;
      return data as Generation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-generations'] });
      toast({
        title: "Generation Created",
        description: "Your generation has been saved successfully.",
      });
    },
    onError: (error) => {
      console.error('Error creating generation:', error);
      toast({
        title: "Error",
        description: "Failed to create generation. Please try again.",
        variant: "destructive",
      });
    }
  });
};

// Hook to update a generation
export const useUpdateGeneration = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Generation> }) => {
      const { data, error } = await supabase
        .from('generations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Generation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['generation', data.id] });
      queryClient.invalidateQueries({ queryKey: ['user-generations'] });
      toast({
        title: "Generation Updated",
        description: "Your changes have been saved.",
      });
    },
    onError: (error) => {
      console.error('Error updating generation:', error);
      toast({
        title: "Error",
        description: "Failed to update generation. Please try again.",
        variant: "destructive",
      });
    }
  });
};

// Hook to delete a generation
export const useDeleteGeneration = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('generations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-generations'] });
      toast({
        title: "Generation Deleted",
        description: "The generation has been removed.",
      });
    },
    onError: (error) => {
      console.error('Error deleting generation:', error);
      toast({
        title: "Error",
        description: "Failed to delete generation. Please try again.",
        variant: "destructive",
      });
    }
  });
};

// Hook to duplicate a generation
export const useDuplicateGeneration = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      // First get the original generation
      const { data: original, error: fetchError } = await supabase
        .from('generations')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Create a duplicate
      const duplicate = {
        ...original,
        id: undefined,
        title: `${original.title || 'Untitled'} (Copy)`,
        public_url_slug: null,
        is_public: false,
        version: 1,
        view_count: 0,
        share_count: 0,
        last_viewed_at: null,
        created_at: undefined,
        updated_at: undefined
      };

      const { data, error } = await supabase
        .from('generations')
        .insert([duplicate])
        .select()
        .single();

      if (error) throw error;

      // Track duplicate event
      await supabase.rpc('track_generation_event', {
        generation_id: original.id,
        event_type: 'duplicate',
        event_data: { new_generation_id: data.id }
      });

      return data as Generation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-generations'] });
      toast({
        title: "Generation Duplicated",
        description: "A copy has been created successfully.",
      });
    },
    onError: (error) => {
      console.error('Error duplicating generation:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate generation. Please try again.",
        variant: "destructive",
      });
    }
  });
};

// Hook to generate public URL
export const useGeneratePublicUrl = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      console.log('Generating public URL for generation:', id);
      
      // Generate a unique slug
      const { data: slug, error: slugError } = await supabase.rpc('generate_public_slug');
      
      if (slugError) {
        console.error('Error generating slug:', slugError);
        throw slugError;
      }

      console.log('Generated slug:', slug);

      const { data, error } = await supabase
        .from('generations')
        .update({ 
          public_url_slug: slug,
          is_public: true 
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating generation:', error);
        throw error;
      }

      console.log('Updated generation:', data);

      // Track share event
      await supabase.rpc('track_generation_event', {
        generation_id: id,
        event_type: 'share'
      });

      return data as Generation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['generation', data.id] });
      toast({
        title: "Public Link Generated",
        description: "Your generation is now publicly accessible.",
      });
    },
    onError: (error) => {
      console.error('Error generating public URL:', error);
      toast({
        title: "Error",
        description: "Failed to generate public link. Please try again.",
        variant: "destructive",
      });
    }
  });
};

// Hook to track analytics events
export const useTrackEvent = () => {
  return useMutation({
    mutationFn: async ({ 
      generationId, 
      eventType, 
      eventData 
    }: { 
      generationId: string; 
      eventType: string; 
      eventData?: any 
    }) => {
      await supabase.rpc('track_generation_event', {
        generation_id: generationId,
        event_type: eventType,
        event_data: eventData
      });
    }
  });
};